import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2, ShieldCheck, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Pricing rules:
// - India          → INR ₹499/month
// - United Kingdom → GBP £4.99/month
// - All other countries (incl. US) → USD $4.99/month
const COUNTRIES = [
  { code: "IN", label: "India",          currency: "INR", price: 499,  full: 999,  symbol: "₹" },
  { code: "GB", label: "United Kingdom", currency: "GBP", price: 4.99, full: 7.99, symbol: "£" },
  { code: "US", label: "United States",  currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "CA", label: "Canada",         currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "AU", label: "Australia",      currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "DE", label: "Germany",        currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "FR", label: "France",         currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "AE", label: "United Arab Emirates", currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "SG", label: "Singapore",      currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
  { code: "OTHER", label: "Other",       currency: "USD", price: 4.99, full: 7.99, symbol: "$" },
];

export default function UpgradeDialog({ open, onOpenChange, currentCountry }) {
  const [country, setCountry] = useState(currentCountry || "GB");
  const [loading, setLoading] = useState(false);

  const selected =
    COUNTRIES.find((c) => c.code === country) || COUNTRIES[1];

  const startCheckout = async () => {
    setLoading(true);
    try {
      await api.billingSetCountry(country).catch(() => {});
      const r = await api.billingCheckout(country, window.location.origin);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || "Stripe checkout failed. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-dialog" className="cre-surface max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span
              data-testid="early-launch-badge"
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-500/10 border border-amber-500/40 rounded-sm px-1.5 py-0.5"
            >
              <Tag className="h-3 w-3" /> Early launch offer
            </span>
          </div>
          <DialogTitle className="font-heading flex items-center gap-2 mt-1.5">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Upgrade to CRE Pro
          </DialogTitle>
          <DialogDescription className="space-y-2 text-slate-600">
            <span className="block">
              Founder launch pricing for teams who want to use CRE beyond trial
              testing.
            </span>
            <span className="block">
              Get 30 CRE reasoning runs per month with structured RCA analysis
              across application, infrastructure, network, database, security,
              support, leadership, and business impact perspectives.
            </span>
            <span className="block">
              Includes stakeholder-ready RCA summaries, CRE Score, reasoning
              trail, priority incident history, and role-wise views for
              Incident Manager, Support/SRE, Leadership, and Management.
            </span>
            <span className="block text-[12px] text-slate-500">
              Best for incident managers, SREs, support leads, management
              teams, and small IT teams handling recurring service issues, RCA
              reviews, or service reviews.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="cre-overline mb-1.5 block">Select your country</label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="upgrade-country-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem
                    key={c.code}
                    value={c.code}
                    data-testid={`upgrade-country-${c.code}`}
                  >
                    {c.label} · {c.symbol}
                    {c.price}/month
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-slate-500">
              India billed in INR. United Kingdom in GBP. All other countries
              (including United States) in USD.
            </p>
          </div>

          <div
            data-testid="upgrade-price-summary"
            className="border border-violet-500/30 bg-violet-500/5 rounded-sm p-4"
          >
            <div className="flex items-center justify-between">
              <div className="cre-overline text-violet-700">CRE Pro</div>
              <span
                data-testid="upgrade-offer-tag"
                className="text-[10px] font-mono uppercase tracking-widest text-amber-700"
              >
                Early launch offer
              </span>
            </div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span
                data-testid="upgrade-price-amount"
                className="font-heading text-3xl font-black text-violet-700"
              >
                {selected.symbol}
                {selected.price}
              </span>
              <span className="text-sm text-slate-500">
                /month · {selected.currency}
              </span>
              <span
                data-testid="upgrade-strike-price"
                className="text-xs text-slate-400 line-through ml-1"
              >
                {selected.symbol}
                {selected.full}
              </span>
            </div>
            <ul className="mt-3 space-y-1 text-xs text-slate-600">
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-600 shrink-0 mt-0.5" />
                <span>30 CRE reasoning runs / month</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-600 shrink-0 mt-0.5" />
                <span>Priority incident history</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-600 shrink-0 mt-0.5" />
                <span>Stakeholder-ready RCA summaries</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-600 shrink-0 mt-0.5" />
                <span>CRE Score and reasoning trail</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-600 shrink-0 mt-0.5" />
                <span>
                  Role-wise views — Incident Manager, Support/SRE, Leadership,
                  Management
                </span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-600 shrink-0 mt-0.5" />
                <span>Cancel anytime via Stripe Customer Portal</span>
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="upgrade-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={startCheckout}
            disabled={loading}
            data-testid="upgrade-dialog-checkout"
            className="cre-cta-primary text-white"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>Continue to Stripe</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
